import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";

const inputStyle = {
  width:"100%", boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:10,
  padding:"9px 12px", color:"#151515", fontSize:13, outline:"none", fontFamily:"inherit", marginBottom:10,
};

const FIELDS = [
  { key: "billing_company_name", label: "Company Name", placeholder: "Acme Inc." },
  { key: "billing_address", label: "Billing Address", placeholder: "123 Main St, Suite 100, City, ST 00000" },
  { key: "accounting_email", label: "Accounting Email", placeholder: "accounting@example.com" },
];

// BillingDetailsSection — the Billing tab's contact-details form (company
// name, billing address, accounting email, DEV-1960). Writes those three
// advertisers columns directly through the Supabase client, scoped by
// effectiveAdvertiserId. Editable by the advertiser themselves — gated by
// canManageBilling, unlike BillingMethodSection which is support-only.
// View mode shows the saved values with a single Edit affordance; Save
// commits and returns to view mode (mirrors the tracking-pixel card pattern
// in UserMenu.jsx).
//
// The initial `advertisers` row is fetched once by UserMenu (shared with
// BillingMethodSection) and passed in as `initialData`/`loading` so the
// Billing tab doesn't issue two separate reads of the same row.
export function BillingDetailsSection({ advertiserId, canManageBilling, initialData, loading }) {
  const [saved, setSaved] = useState({ billing_company_name: "", billing_address: "", accounting_email: "" });
  const [draft, setDraft] = useState(saved);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    const next = {
      billing_company_name: initialData?.billing_company_name ?? "",
      billing_address: initialData?.billing_address ?? "",
      accounting_email: initialData?.accounting_email ?? "",
    };
    setSaved(next);
    setDraft(next);
  }, [initialData, loading]);

  const startEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!supabase || !advertiserId || !canManageBilling) return;
    setSaving(true);
    setError(null);
    try {
      const { error: saveError } = await supabase
        .from("advertisers")
        .update({
          billing_company_name: draft.billing_company_name || null,
          billing_address: draft.billing_address || null,
          accounting_email: draft.accounting_email || null,
        })
        .eq("id", advertiserId);
      if (saveError) {
        setError(saveError.message || "Failed to save billing details");
        return;
      }
      setSaved(draft);
      setEditing(false);
    } catch (err) {
      setError(err?.message ?? "Failed to save billing details");
    } finally {
      setSaving(false);
    }
  };

  const hasAnyValue = FIELDS.some(({ key }) => !!saved[key]);

  return (
    <div style={{ background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:12, padding:16, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1 }}>Billing Details</div>
        {!loading && !editing && canManageBilling && (
          <button
            type="button"
            onClick={startEdit}
            style={{ padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}
          >
            Edit
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>Loading…</div>
      ) : editing ? (
        <>
          {FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{label}</div>
              <input
                value={draft[key]}
                onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                disabled={saving}
                placeholder={placeholder}
                style={inputStyle}
              />
            </div>
          ))}

          {error && (
            <div style={{ marginBottom:10, fontSize:12, color:"#e53935" }}>{error}</div>
          )}

          <div style={{ display:"flex", gap:8, marginTop:2 }}>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{ flex:1, padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor: saving ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{ flex:1, padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      ) : hasAnyValue ? (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {FIELDS.map(({ key, label }) => (
            <div key={key}>
              <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{label}</div>
              <div style={{ fontSize:13, color: saved[key] ? "#3E3E3E" : "#7b7b7b" }}>{saved[key] || "—"}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>
          {canManageBilling ? "No billing details on file yet." : "No billing details on file."}
        </div>
      )}
    </div>
  );
}
